/**
 * QboTaxCodeMapSettings (R2 — SPEC-qbo-csv-harden-2026-06-20)
 *
 * Admin form for the per-tenant QBO tax-code map. Lists the current
 * Keystone-tax-code -> QBO-tax-code mappings and supports add / edit / remove,
 * persisting the WHOLE map via PUT /api/me/settings/qbo-tax-code-map. Lives in
 * the Integrations tab beside the QuickBooks setup so it's coherent with the
 * QBO/CSV export feature.
 *
 * Field discipline: the editor holds the full map as local rows and the save
 * sends the entire reconstructed map object (no per-key field-pick), so every
 * entry round-trips and a removed row is genuinely dropped on save. This is the
 * map read at export time by services/qbo_csv_export.py.
 */
import { useState, useEffect } from 'react';
import { Receipt, Plus, Trash2 } from 'lucide-react';
import {
  useQboTaxCodeMap,
  useUpdateQboTaxCodeMap,
  type QboTaxCodeMap,
} from '@/hooks/useQboTaxCodeMap';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface MapRow {
  /** Stable client id so editing a key doesn't remount the row. */
  rowId: string;
  keystoneCode: string;
  qboCode: string;
}

let _rowSeq = 0;
function newRowId(): string {
  _rowSeq += 1;
  return `row-${_rowSeq}-${Date.now()}`;
}

function mapToRows(map: QboTaxCodeMap): MapRow[] {
  return Object.entries(map).map(([keystoneCode, qboCode]) => ({
    rowId: newRowId(),
    keystoneCode,
    qboCode,
  }));
}

/**
 * Rebuild the full map object from the editor rows. Blank-key/blank-value rows
 * are dropped (matches the backend's strip). Last write wins on duplicate keys.
 */
function rowsToMap(rows: MapRow[]): QboTaxCodeMap {
  const map: QboTaxCodeMap = {};
  for (const row of rows) {
    const k = row.keystoneCode.trim();
    const v = row.qboCode.trim();
    if (k && v) map[k] = v;
  }
  return map;
}

export function QboTaxCodeMapSettings() {
  const { data, isLoading } = useQboTaxCodeMap();
  const updateMap = useUpdateQboTaxCodeMap();

  const [rows, setRows] = useState<MapRow[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize from fetched data
  useEffect(() => {
    if (data) {
      setRows(mapToRows(data));
      setHasChanges(false);
    }
  }, [data]);

  const handleAddRow = () => {
    setRows((prev) => [...prev, { rowId: newRowId(), keystoneCode: '', qboCode: '' }]);
    setHasChanges(true);
  };

  const handleEditRow = (rowId: string, field: 'keystoneCode' | 'qboCode', value: string) => {
    setRows((prev) =>
      prev.map((r) => (r.rowId === rowId ? { ...r, [field]: value } : r)),
    );
    setHasChanges(true);
  };

  const handleRemoveRow = (rowId: string) => {
    setRows((prev) => prev.filter((r) => r.rowId !== rowId));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Send the WHOLE reconstructed map — every remaining entry, nothing else.
    updateMap.mutate(rowsToMap(rows), {
      onSuccess: () => setHasChanges(false),
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-3 py-2" data-testid="qbo-tax-code-map-loading">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-9 w-24" />
      </div>
    );
  }

  return (
    <div className="space-y-4 py-2" data-testid="qbo-tax-code-map-settings">
      <div>
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Receipt className="w-4 h-4 text-blue-600" />
          QBO Tax-Code Map
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Map each Keystone tax rate to the exact QBO tax-code name so the CSV
          export tags every line correctly. Rates with no mapping export as
          "Out of scope". Enter the rate as a percentage (e.g. <code>5.00</code>)
          and the QBO tax-code name exactly as it appears in QuickBooks (e.g.{' '}
          <code>GST</code>).
        </p>
      </div>

      {/* Mapping rows */}
      {rows.length === 0 ? (
        <div
          className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-md p-4 text-center"
          data-testid="qbo-tax-code-map-empty"
        >
          No tax-code mappings yet. Add one to tag exported invoice lines.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 px-1">
            <span className="flex-1">Keystone tax rate (%)</span>
            <span className="flex-1">QBO tax-code name</span>
            <span className="w-9" />
          </div>
          {rows.map((row) => (
            <div key={row.rowId} className="flex items-center gap-2" data-testid="qbo-tax-code-row">
              <input
                type="text"
                value={row.keystoneCode}
                onChange={(e) => handleEditRow(row.rowId, 'keystoneCode', e.target.value)}
                placeholder="5.00"
                aria-label="Keystone tax rate"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="text"
                value={row.qboCode}
                onChange={(e) => handleEditRow(row.rowId, 'qboCode', e.target.value)}
                placeholder="GST"
                aria-label="QBO tax-code name"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => handleRemoveRow(row.rowId)}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                aria-label={`Remove mapping ${row.keystoneCode || 'row'}`}
                data-testid="qbo-tax-code-remove"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add + Save */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleAddRow}
          className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          data-testid="qbo-tax-code-add"
        >
          <Plus className="w-4 h-4" />
          Add mapping
        </button>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updateMap.isPending}
          size="sm"
          data-testid="qbo-tax-code-save"
        >
          {updateMap.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
